import type { GetStaticPropsContext } from 'next';

import { Flex } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { FooterBannerSection } from '~/features/page-template/templates/partnerships/components/footer-banner-section';
import { HeroSection } from '~/features/page-template/templates/partnerships/components/hero-section';
import { OutcomesSection } from '~/features/page-template/templates/partnerships/components/outcomes-section';
import { WaysToPartnerSection } from '~/features/page-template/templates/partnerships/components/ways-to-partner-section';
import {
  PartnershipsTemplate,
  partnershipsTemplateQuery,
  partnershipsTemplatesSchema,
} from '~/features/page-template/templates/partnerships/model';

export default function PartnershipPage({
  page,
  partnershipsTemplate,
}: {
  page: Page;
  partnershipsTemplate: PartnershipsTemplate;
}) {
  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <HeroSection
          image={partnershipsTemplate.heroImage}
          mobileImage={partnershipsTemplate.heroImageMobile}
          subTitle={partnershipsTemplate.pageSubtitle}
          title={partnershipsTemplate.pageTitle}
          ctaSlot={
            <ButtonLink
              buttonProps={{
                variant: 'solid',
                colorScheme: 'sea-foam',
                width: {
                  base: '100%',
                  md: 'fit-content',
                },
                onClick: () =>
                  trackEvent({
                    type: 'click',
                    name: 'Enterprise CTA Clicked',
                    label: 'contact_us_clicked',
                    value: 'CTA 1',
                  }),
              }}
              linkProps={{
                href: '/get-started/partnerships',
              }}
            >
              {partnershipsTemplate.heroCTALabel}
            </ButtonLink>
          }
        />
        <OutcomesSection
          outcomes={partnershipsTemplate.partnershipOutcomesList}
          title={partnershipsTemplate.partnershipOutcomesTitle}
        />
        <WaysToPartnerSection
          title={partnershipsTemplate.waysToPartnerTitle}
          waysToPartner={partnershipsTemplate.waysToPartnerList}
          ctaSlot={
            <Flex>
              <ButtonLink
                linkProps={{ href: '/get-started/partnerships' }}
                buttonProps={{
                  rightIcon: <ArrowRightIcon h={6} w={6} />,
                  w: { base: 'full', md: 'fit-content' },
                  mx: 'auto',
                  onClick: () =>
                    trackEvent({
                      type: 'click',
                      name: 'Enterprise CTA Clicked',
                      label: 'contact_us_clicked',
                      value: 'CTA 2',
                    }),
                }}
              >
                {partnershipsTemplate.waysToPartnerCTALabel}
              </ButtonLink>
            </Flex>
          }
        />
        <FooterBannerSection
          partners={partnershipsTemplate.partners}
          title={partnershipsTemplate.footerBannerTitle}
          ctaSlot={
            <Flex>
              <ButtonLink
                buttonProps={{
                  variant: 'solid',
                  colorScheme: 'sea-foam',
                  width: {
                    base: '100%',
                    md: 'fit-content',
                  },
                  onClick: () =>
                    trackEvent({
                      type: 'click',
                      name: 'Enterprise CTA Clicked',
                      label: 'contact_us_clicked',
                      value: 'CTA 3',
                    }),
                  mx: 'auto',
                }}
                linkProps={{
                  href: '/get-started/partnerships',
                }}
              >
                {partnershipsTemplate.footerCTALabel}
              </ButtonLink>
            </Flex>
          }
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, partnershipsTemplate] = await Promise.all([
    getPage('/'),
    getPageTemplate<PartnershipsTemplate>({
      templateName: 'Partnerships Page',
      templateQuery: partnershipsTemplateQuery,
      templateZodSchema: partnershipsTemplatesSchema,
    }),
  ]);

  return {
    props: {
      page,
      partnershipsTemplate,
    },
  };
}
